/**
 * EmailReplyBar (EMAIL-PARITY) — the email sibling of InlineComposer: a docked
 * composer at the bottom of an EMAIL conversation so email feels identical to
 * texting (Seth: "will emails feel/act the same?"). Same quick actions (Insert
 * template, Insert field) + a subject line. The recipient is the thread's email,
 * so no recipient picker — replies go straight back. Sends via send-email
 * (consent + comms-gate enforced server-side).
 */
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Send, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { TemplatePicker } from "@/features/customer-360/CustomerMessages";
import { useCustomerMergeContext, MergeFieldMenu } from "@/lib/mergeFields";

export function EmailReplyBar({
  custno,
  endpoint,
  name,
  providerName,
  threadSubject,
}: {
  custno: string;
  endpoint: string | null;
  name: string;
  providerName?: string | null;
  threadSubject?: string | null;
}) {
  const qc = useQueryClient();
  const { data: ctx } = useCustomerMergeContext(custno, providerName ?? name);
  const [subject, setSubject] = useState(
    threadSubject ? (/^re:/i.test(threadSubject) ? threadSubject : `Re: ${threadSubject}`) : "",
  );
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [gated, setGated] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const to = (endpoint ?? "").trim();
  const greet = ctx?.firstName ?? (name.split(/[\s,]/)[0] || "there");

  const insertToken = (token: string) => {
    const el = taRef.current;
    if (!el) {
      setBody((b) => b + token);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const send = async () => {
    const text = body.trim();
    if (!to || !text || sending) return;
    setSending(true);
    setErr(null);
    setGated(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: { to: [to], subject: subject.trim() || "(no subject)", text: body, custno, message_type: "scheduling" },
      });
      const detail = (data as { error?: string; blocked?: string } | null) ?? null;
      if (detail?.blocked === "testing" || /testing|paused/i.test(detail?.error ?? "")) {
        setGated("Comms are in testing — at go-live this sends for real. Nothing went to the customer.");
        return;
      }
      if (error || detail?.error) {
        setErr(error?.message ?? detail?.error ?? "send failed");
        return;
      }
      setBody("");
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["conversation_messages"] }),
        qc.invalidateQueries({ queryKey: ["messages"] }),
        qc.invalidateQueries({ queryKey: ["inbox_conversations"] }),
      ]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-t border-hairline bg-white px-4 py-3">
      {gated && <p className="mb-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">{gated}</p>}
      {err && <p className="mb-2 rounded-md bg-red-50 px-2.5 py-1.5 text-xs text-red-700">Send failed: {err}</p>}
      <div className="rounded-xl border border-hairline bg-white transition-colors focus-within:border-brand-400">
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          className="block w-full rounded-t-xl border-b border-hairline bg-transparent px-3 py-2 text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none"
        />
        <textarea
          ref={taRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder={`Email ${greet} or use a quick action…`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          className="block max-h-72 min-h-[5rem] w-full resize-y bg-transparent px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
        />
        {/* pr clears the two fixed bottom-right bubbles (Ask Pegasus, team chat) so Send is never under them (INBOX-HD). */}
        <div className="flex items-center justify-between gap-2 py-1.5 pl-2 pr-[7.5rem]">
          <div className="flex flex-wrap items-center gap-0.5">
            <TemplatePicker
              custno={custno}
              dropUp
              onPick={(b, subj) => {
                setBody(b);
                if (subj) setSubject(subj);
              }}
            />
            <MergeFieldMenu ctx={ctx} dropUp onInsert={insertToken} label="Field" />
          </div>
          <button
            type="button"
            onClick={send}
            disabled={sending || !body.trim()}
            className={cn(
              "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-brand-700 px-4 text-[12.5px] font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:opacity-50",
            )}
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send
          </button>
        </div>
      </div>
      <p className="mt-1 text-[11px] text-slate-400">Sends as Sedgwick Heating · lands on this thread · ⌘/Ctrl+Enter to send</p>
    </div>
  );
}
